import React, { useEffect, useState, useCallback } from 'react';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { Anchor, Save, RotateCcw, Plus, Trash2, Check, AlertTriangle } from 'lucide-react';
import { DEFAULT_IGNORE_REASONS } from './ActualCost2Ignore';
import { US_WAREHOUSES } from '../../../shared/utils/usWarehouses'; // CHA-302 — this is the WRITE path: a Save here persists the scope, so a local copy re-arms the drift

/**
 * ActualCost2Settings — CHA-323 phase 2. Editable scope + warehouse→coast mapping for the
 * Actual Cost 2 reconciliation, stored in Firestore at `actual_cost_2_config/{tenantId}`
 * (authed-CRUD, same contract as the other editable lookups — firestore.rules). Direct client
 * read/write (getDoc/setDoc) like warehouse_fees / us_holidays; no BigQuery CRUD.
 *
 * These values drive which received invoices the reconciliation considers (phases 3-4 read them).
 */

const CONFIG_COLLECTION = 'actual_cost_2_config';

// Defaults mirror the server's DEFAULT_SCOPE (functions/actualCost2.js). These two MUST stay in step:
// this object is the WRITE path — saving Settings persists it to actual_cost_2_config/{tenantId},
// which loadScope then PREFERS over the server default. So a stale copy here does not merely display
// the wrong scope, it silently overwrites the real one the next time the operator saves for any
// reason (CHA-426 self-review: leaving POL/SCG out here would have re-armed an $8M regression on one
// unrelated click). Warehouse codes: the trailing letter is the location — PO-L = Los Angeles (west),
// PO-N = New Jersey (east), SCG = Georgia (east), SCN = Nevada (west).
export const DEFAULT_AC2_CONFIG = {
  warehouses: US_WAREHOUSES,
  productionTypes: ['CPLAN'],
  divisions: ['10', '15', '41', '45', '60', '61', '77', '78'],
  importerCodes: ['INMOCENBS'], // BWI + IMGA to be added later per operator
  ignoreReasons: DEFAULT_IGNORE_REASONS, // CHA-379 — reasons offered when ignoring an invoice
};

// Normalize a loaded doc into the full shape (missing keys fall back to defaults).
function normalizeConfig(raw) {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_AC2_CONFIG };
  return {
    warehouses: Array.isArray(raw.warehouses) && raw.warehouses.length
      ? raw.warehouses.filter((w) => w && w.code).map((w) => ({ code: String(w.code).trim().toUpperCase(), coast: w.coast === 'east' ? 'east' : 'west' }))
      : DEFAULT_AC2_CONFIG.warehouses,
    productionTypes: Array.isArray(raw.productionTypes) ? raw.productionTypes.map(String) : DEFAULT_AC2_CONFIG.productionTypes,
    divisions: Array.isArray(raw.divisions) ? raw.divisions.map(String) : DEFAULT_AC2_CONFIG.divisions,
    importerCodes: Array.isArray(raw.importerCodes) ? raw.importerCodes.map(String) : DEFAULT_AC2_CONFIG.importerCodes,
    // CHA-379 — empty list falls back to defaults (an empty select would make ignoring impossible).
    ignoreReasons: Array.isArray(raw.ignoreReasons) && raw.ignoreReasons.map((s) => String(s).trim()).filter(Boolean).length
      ? raw.ignoreReasons.map((s) => String(s).trim()).filter(Boolean)
      : DEFAULT_AC2_CONFIG.ignoreReasons,
  };
}

// A tiny comma/space-separated list editor (used for the scope fields).
function ListEditor({ label, hint, values, onChange, upper = true, commaOnly = false }) {
  const [text, setText] = useState(values.join(', '));
  useEffect(() => { setText(values.join(', ')); }, [values]);
  const commit = () => {
    // commaOnly keeps multi-word entries intact (CHA-379 ignore reasons like "prepaid by vendor").
    const parts = text.split(commaOnly ? ',' : /[,\s]+/).map((s) => (upper ? s.trim().toUpperCase() : s.trim())).filter(Boolean);
    onChange(Array.from(new Set(parts)));
  };
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700">{label}</label>
      {hint && <p className="text-xs text-slate-500 mt-0.5 mb-1">{hint}</p>}
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-teal-500"
        placeholder="comma-separated"
      />
    </div>
  );
}

export default function ActualCost2Settings({ userProfile }) {
  const tenantId = userProfile?.tenantId || 'inmocean';
  const [config, setConfig] = useState(DEFAULT_AC2_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null); // {type:'ok'|'err', msg}

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(getFirestore(), CONFIG_COLLECTION, tenantId));
        if (cancelled) return;
        setConfig(snap.exists() ? normalizeConfig(snap.data()) : DEFAULT_AC2_CONFIG);
      } catch (e) {
        if (!cancelled) { setConfig(DEFAULT_AC2_CONFIG); setStatus({ type: 'err', msg: `Could not load saved settings (${e.message}) — showing defaults.` }); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId]);

  const save = useCallback(async () => {
    setSaving(true); setStatus(null);
    try {
      await setDoc(doc(getFirestore(), CONFIG_COLLECTION, tenantId), { ...config, updated_at: new Date().toISOString() }, { merge: true });
      setStatus({ type: 'ok', msg: 'Settings saved.' });
    } catch (e) {
      setStatus({ type: 'err', msg: `Save failed: ${e.message}` });
    } finally {
      setSaving(false);
    }
  }, [config, tenantId]);

  const setWarehouse = (i, patch) => setConfig((c) => ({ ...c, warehouses: c.warehouses.map((w, idx) => (idx === i ? { ...w, ...patch } : w)) }));
  const addWarehouse = () => setConfig((c) => ({ ...c, warehouses: [...c.warehouses, { code: '', coast: 'west' }] }));
  const removeWarehouse = (i) => setConfig((c) => ({ ...c, warehouses: c.warehouses.filter((_, idx) => idx !== i) }));

  if (loading) return <div className="text-sm text-slate-500 p-4">Loading settings…</div>;

  return (
    <div className="space-y-5">
      {/* Warehouse -> coast */}
      <div className="rounded-lg border border-slate-200 bg-white p-5">
        <div className="flex items-center gap-2 mb-1">
          <Anchor className="h-4 w-4 text-slate-500" />
          <h3 className="text-base font-semibold text-slate-800 m-0">Warehouse → coast</h3>
        </div>
        <p className="text-xs text-slate-500 mb-3">Which warehouse code routes through which coast broker. Drives the dual-coast uploads and the reconciliation scope.</p>
        <div className="space-y-1.5">
          {config.warehouses.map((w, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                value={w.code}
                onChange={(e) => setWarehouse(i, { code: e.target.value.toUpperCase() })}
                className="w-28 border border-slate-300 rounded px-2 py-1 text-sm font-mono uppercase focus:outline-none focus:ring-1 focus:ring-teal-500"
                placeholder="CODE"
              />
              <div className="inline-flex rounded border border-slate-300 overflow-hidden">
                {['west', 'east'].map((coast) => (
                  <button
                    key={coast}
                    onClick={() => setWarehouse(i, { coast })}
                    className={`px-3 py-1 text-xs font-medium capitalize ${w.coast === coast ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                  >
                    {coast}
                  </button>
                ))}
              </div>
              <button onClick={() => removeWarehouse(i)} className="text-slate-400 hover:text-red-600 p-1" title="Remove">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <button onClick={addWarehouse} className="mt-2 inline-flex items-center gap-1 text-sm text-teal-700 hover:text-teal-800">
          <Plus className="h-4 w-4" /> Add warehouse
        </button>
      </div>

      {/* Scope filters */}
      <div className="rounded-lg border border-slate-200 bg-white p-5 space-y-4">
        <h3 className="text-base font-semibold text-slate-800 m-0">In-scope filters</h3>
        <ListEditor label="Production types" hint="Only these production types are reconciled (default: CPLAN)." values={config.productionTypes} onChange={(v) => setConfig((c) => ({ ...c, productionTypes: v }))} />
        <ListEditor label="Divisions" hint="InMocean divisions in scope." values={config.divisions} onChange={(v) => setConfig((c) => ({ ...c, divisions: v }))} upper={false} />
        <ListEditor label="Importer codes" hint="Importer-of-record codes (not yet applied to the reconciliation — activates with BWI / IMGA support)." values={config.importerCodes} onChange={(v) => setConfig((c) => ({ ...c, importerCodes: v }))} />
        <ListEditor label="Ignore reasons" hint="Choices offered when ignoring a commercial invoice from the missing list (small / prepaid / parcel shipments that will never get a broker job#). Comma-separated; multi-word reasons allowed." values={config.ignoreReasons} onChange={(v) => setConfig((c) => ({ ...c, ignoreReasons: v }))} upper={false} commaOnly />
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded">
          <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save settings'}
        </button>
        <button onClick={() => setConfig(DEFAULT_AC2_CONFIG)} className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-800 px-2 py-2">
          <RotateCcw className="h-4 w-4" /> Reset to defaults
        </button>
        {status && (
          <span className={`inline-flex items-center gap-1 text-sm ${status.type === 'ok' ? 'text-green-700' : 'text-red-700'}`}>
            {status.type === 'ok' ? <Check className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
            {status.msg}
          </span>
        )}
      </div>
    </div>
  );
}
